import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ParagraphText, UBButton } from "../../../../Libraries";
import { TitleAndActionBox } from "../../../common";
import { Box, Grid } from "@mui/material";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useLocation, useNavigate } from "react-router-dom";
import VouchersPaymentForm from "./VouchersPaymentForm";
import VouchersReviewSection from "./VouchersReviewSection";
import { useWalletContext } from "../../../../context/WalletContext";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { uuid } from "../../../../constants";
import { Sync } from "../../../../SyncService";
import { useAuthContext } from "../../../../context/AuthContext";
import { handleApi } from "../../../../apis";
import {
  GETWALLETVOUCHERPAYMENT,
  POSTWALLETVOUCHERPAYMENT,
} from "../../../../apis/configuration";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { formatNum } from "../../../../oscar-pos-core/constants";
import BusinessLoader from "../../../common/BusinessLoader";
import withObservables from "@nozbe/with-observables";
import { getCustomerOfBusiness } from "../../../../database/helpers/customer";
import formStyles from "../../SaleExpense/SaleExpenseForm/styles.module.css";
import svStyles from "./SellVouchers.module.css";
import NonIdealNew from "../../../common/NonIdealNew";
import { v4 as uuidv4 } from "uuid";
import { EnterIcon } from "../../../../assets/icons/icon";
import { getCategoriesByType } from "../../../../utils";
import VerifyPinModal from "../../../common/Modals/VerifyPinModal";

const SellVouchers = ({ customers }) => {
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const { state: locationState } = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);
  const [state, setState] = useState(() => ({
    voucherCompany: locationState?.tab || "",
    voucherCompanyId: "",
    voucherId: "",
    voucherObj: null,
    customerName: locationState?.prefilledCustomerName || "",
    customerPhone: locationState?.prefilledCustomerPhone || "",
    expenseCategory: "",
    expenseCategoryId: "",
    paymentStatus: "Paid",
    paymentMode: "Cash",
  }));
  const [customer, setCustomer] = useState(null);
  const idempotencyKey = useRef(uuidv4());
  const lastSubmittedRef = useRef({ voucherId: "" });
  const walletVoucherTimeoutId = useRef(null);
  const isMounted = useRef(true);

  const {
    fetchWalletData,
    walletData,
    fetchVochureList,
    vochureIsLoading,
    handleFetchPopularVouchers,
    popularVouchers,
    popularVouchersLoading,
    handleGetWalletBalance,
    fetchAccountBalanceLoading,
  } = useWalletContext();

  const balance = walletData?.current_balance || 0;
  const availableVouchers = walletData?.available_vouchers || [];
  const earningRules = walletData?.voucher_earning_rules || {};
  const partnerStatus = walletData?.account_info?.partner_status || "";
  const { showSnackbar } = useSnackbar();
  const { user } = useAuthContext();

  // Guard: when backend has no vouchers, send user back to the screen they came from,
  // or to UCash on direct link / when that screen is itself a Sell Vouchers page
  const [isCheckingVouchers, setIsCheckingVouchers] = useState(
    availableVouchers.length === 0,
  );

  useEffect(() => {
    handleFetchPopularVouchers(navigate);
    if (!availableVouchers || availableVouchers.length === 0) {
      fetchVochureList(navigate).then((vouchers) => {
        if (!isMounted.current) return;
        if (!vouchers || vouchers.length === 0) {
          const from = locationState?.from?.toLowerCase() || "";
          const canGoBackToFrom =
            from &&
            !from.startsWith("/ucash/vouchers") &&
            window.history.state?.idx > 0;
          if (canGoBackToFrom) navigate(-1);
          else navigate("/UCash", { replace: true });
          return;
        }
        setIsCheckingVouchers(false);
      });
    }
    return () => {
      isMounted.current = false;
      clearTimeout(walletVoucherTimeoutId.current);
    };
  }, []);


  const categoryList = useMemo(() => getCategoriesByType("vouchers"), []);

  // Earning points for vouchers: rule value is a percentage of the voucher price
  const earnPoints = useMemo(() => {
    const price = state.voucherObj?.discounted_price ?? state.voucherObj?.price;
    if (!state.voucherId || !partnerStatus || !price) return 0;
    const percentage =
      earningRules?.[String(state.voucherId)]?.[partnerStatus]?.voucher || 0;
    return Math.floor(percentage * price);
  }, [earningRules, state.voucherId, partnerStatus, state.voucherObj]);

  const isFormDisabled = !(
    state.voucherCompany &&
    state.voucherId &&
    state.voucherObj &&
    state.customerName
  );

  const isReviewDisabled =
    !fetchAccountBalanceLoading &&
    balance <
      (state.voucherObj?.discounted_price ?? state.voucherObj?.price ?? 0);

  useEffect(() => {
    if (showReview) {
      handleGetWalletBalance(navigate).finally(() => setIsLoading(false));
    }
  }, [showReview]);

  const handlePaybillClick = () => {
    if (
      balance <
      (state.voucherObj?.discounted_price ?? state.voucherObj?.price ?? 0)
    ) {
      showSnackbar({
        message: I18n.insufficient_balance_please_try_different_amount,
        severity: "error",
        icon: "",
      });
      return;
    }
    setIsVerifyOpen(true);
  };

  const handlePaybill = async () => {
    setIsLoading(true);
    triggerLogEvent(analytics, "w_earn_vouchers_sell");
    try {
      const payload = {
        voucher_id: state.voucherId,
        customer_name: state.customerName,
        customer_phone: state.customerPhone,
        idempotency_key: idempotencyKey.current,
        category_id: state.expenseCategoryId || "",
        payment_mode:
          state.paymentStatus.toLowerCase() == "paid"
            ? state.paymentMode?.toLowerCase()
            : "",
      };
      const response = await handleApi(
        POSTWALLETVOUCHERPAYMENT,
        payload,
        null,
        navigate,
      );
      if (response?.success) {
        fetchWalletVoucherTransaction(response?.job_id);
      } else {
        setIsLoading(false);
        showSnackbar({
          message: response?.message || I18n.voucher_purchase_failed,
          severity: "error",
          icon: "",
        });
      }
    } catch (error) {
      setIsLoading(false);
      showSnackbar({
        message: error?.response?.data?.message || I18n.error_in_buy_voucher,
        severity: "error",
        icon: "",
      });
    }
  };

  const fetchWalletVoucherTransaction = async (id) => {
    try {
      const response = await handleApi(
        GETWALLETVOUCHERPAYMENT,
        { job_id: id },
        null,
        navigate,
      );
      if (response?.status === "COMPLETED") {
        if (walletVoucherTimeoutId.current)
          clearTimeout(walletVoucherTimeoutId.current);
        const transaction_details = response?.transaction_details || {};
        const data = {
          amount: Math.abs(
            transaction_details?.amount || response?.amount || 0,
          ),
          logo: transaction_details?.icon || response?.icon || "",
          date: transaction_details?.created_at,
          commission: transaction_details?.commission || 0,
          processingTime:
            response?.processing_time ||
            transaction_details?.processing_time ||
            "",
        };
        try {
          if (state.paymentStatus === "Unpaid" && customer) {
            await customer.createPayment({
              uid: uuid(),
              amount: Number(data.amount),
              payment_mode: "Udhaar",
              date: new Date(),
              udhaar_note: "voucher",
              imageURL: null,
              outstanding:
                (customer.total_outstanding_payment || 0) + data.amount,
              external_type: "voucher",
            });
            Sync(user?.phone_number, navigate);
          }
        } catch {}
        triggerLogEvent(analytics, "w_earn_vouchers_confirm");
        fetchWalletData({ navigate });
        if (!isMounted.current) return;
        setIsLoading(false);
        navigate(`/UCash/Vouchers/TransactionSuccess/${id}`, {
          state: { data, transaction: transaction_details },
        });
      } else if (response?.status === "FAILED") {
        if (walletVoucherTimeoutId.current)
          clearTimeout(walletVoucherTimeoutId.current);
        if (!isMounted.current) return;
        setIsLoading(false);
        showSnackbar({
          message: response?.message || I18n.voucher_transaction_failed,
          severity: "error",
          icon: "",
        });
      } else if (isMounted.current) {
        walletVoucherTimeoutId.current = setTimeout(
          () => fetchWalletVoucherTransactionRef.current(id),
          5000,
        );
      }
    } catch (error) {
      if (!isMounted.current) return;
      if (walletVoucherTimeoutId.current)
        clearTimeout(walletVoucherTimeoutId.current);
      setIsLoading(false);
      showSnackbar({
        message: error?.response?.data?.message || I18n.error_in_buy_voucher,
        severity: "error",
        icon: "",
      });
    }
  };

  const onContinue = () => {
    triggerLogEvent(analytics, "w_earn_vouchers_continue");
    // Regenerate idempotency key only if the voucher changed since the last continue
    if (state.voucherId !== lastSubmittedRef.current.voucherId) {
      idempotencyKey.current = uuidv4();
    }
    lastSubmittedRef.current = { voucherId: state.voucherId };
    setIsLoading(true);
    setShowReview(true);
  };

  const fetchWalletVoucherTransactionRef = useRef(null);
  fetchWalletVoucherTransactionRef.current = fetchWalletVoucherTransaction;
  const handlePaybillRef = useRef(handlePaybillClick);
  handlePaybillRef.current = handlePaybillClick;
  const onContinueRef = useRef(onContinue);
  onContinueRef.current = onContinue;

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.shiftKey && event.key === "Enter") {
        if (showReview && !(isReviewDisabled || isLoading)) {
          event.preventDefault();
          handlePaybillRef.current();
        } else if (!showReview && !(isFormDisabled || isLoading)) {
          event.preventDefault();
          onContinueRef.current();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showReview, isFormDisabled, isReviewDisabled, isLoading]);

  return (
    <Fragment>
      <Grid
        container
        className={`${formStyles.root} ${formStyles.SaleExpenseFormWrapper} ${svStyles.sellVouchersWrapper}`}
      >
        <BusinessLoader
          loadBusiness={isLoading || vochureIsLoading || isCheckingVouchers}
        />
        {isCheckingVouchers ? null : availableVouchers.length > 0 ? (
          <Grid item md={12}>
            <TitleAndActionBox
              data={{
                title: showReview ? I18n.confirm_payment : I18n.vouchers,
                isBack: true,
                onBack: isLoading
                  ? () => {}
                  : showReview
                    ? () => setShowReview(false)
                    : () => navigate(-1),
                id: "sell_vouchers",
              }}
            />

            <Box className={formStyles.formBodyGrid}>
              <Box className={formStyles.formColumn}>
                <Box
                  className={`${formStyles.formBody} expenseFormBody ${svStyles.sellVouchersFormBody}`}
                >
                  {showReview ? (
                    <VouchersReviewSection
                      I18n={I18n}
                      state={state}
                      setState={setState}
                      categoryList={categoryList}
                      balance={balance}
                      balanceLoading={fetchAccountBalanceLoading}
                      earnPoints={earnPoints}
                    />
                  ) : (
                    <VouchersPaymentForm
                      I18n={I18n}
                      popularVouchers={popularVouchers}
                      popularVouchersLoading={popularVouchersLoading}
                      availableVouchers={availableVouchers}
                      state={state}
                      setState={setState}
                      setCustomer={setCustomer}
                      customers={customers}
                      navigate={navigate}
                    />
                  )}

                  <UBButton
                    id="sell_vouchers_primary_action"
                    title={<ParagraphText>{I18n.next}</ParagraphText>}
                    iconComponent={<EnterIcon />}
                    iconPlacement="right"
                    loading={isLoading}
                    disabled={
                      showReview
                        ? isReviewDisabled || isLoading
                        : isFormDisabled || isLoading
                    }
                    className={`buttonPrimary saveSaleButton ${svStyles.sellVouchersPrimaryAction}`}
                    onClick={showReview ? handlePaybillClick : onContinue}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>
        ) : !vochureIsLoading ? (
          <NonIdealNew
            title={I18n.no_vouchers_available}
            subtitle={I18n.there_are_no_vouchers_available_at_the_moment}
          />
        ) : null}
      </Grid>

      <VerifyPinModal
        open={isVerifyOpen}
        handleClose={() => setIsVerifyOpen(false)}
        amount={state.voucherObj?.discounted_price ?? state.voucherObj?.price ?? 0}
        onVerified={handlePaybill}
        forgotPinAnalyticsEvent="w_earn_vouchers_forgot_pin"
      />
    </Fragment>
  );
};

const enhance = withObservables([], () => ({
  customers: getCustomerOfBusiness(
    typeof window !== "undefined"
      ? localStorage.getItem("business_id") || ""
      : "",
  ),
}));

export default enhance(SellVouchers);
